import { useEffect, useMemo, useRef, useState } from 'react';

function lastNameKey(name = '') {
	const parts = name.trim().toLowerCase().split(/\s+/);
	return parts.length > 1 ? parts[parts.length - 1] : parts[0];
}

function todayYMD() {
	const d = new Date();
	const pad = (n) => String(n).padStart(2, '0');
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDateLabel(ymd) {
	if (!ymd) return '';
	const [y, m, d] = ymd.split('-').map(Number);
	if (!y || !m || !d) return ymd;
	const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thurs', 'Fri', 'Sat'];
	const date = new Date(y, m - 1, d);
	return `${days[date.getDay()]} ${months[m - 1]} ${d}`;
}

function Field({ label, children }) {
	return (
		<div style={{ display: 'grid', gap: 6 }}>
			<div style={{ fontWeight: 700 }}>{label}</div>
			{children}
		</div>
	);
}

function DayCard({ day }) {
	return (
		<div
			style={{
				border: '1px solid #c6ced0',
				borderRadius: 12,
				padding: 12,
				background: 'rgba(255, 255, 255, 0.92)',
				boxShadow: '0 10px 24px rgba(32, 65, 81, 0.12)',
				display: 'grid',
				gap: 10,
			}}
		>
			<div style={{ fontWeight: 800 }}>{formatDateLabel(day.date)}</div>
			<div style={{ display: 'grid', gap: 8 }}>
				<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
					<div style={{ fontWeight: 700, minWidth: 60, whiteSpace: 'nowrap' }}>
						{day.am?.startTime || 'AM'}
					</div>
					<div>
						{day.am ? (
							<span>{day.am.activity || '—'}</span>
						) : (
							'—'
						)}
					</div>
				</div>
				<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
					<div style={{ fontWeight: 700, minWidth: 60, whiteSpace: 'nowrap' }}>
						{day.pm?.startTime || 'PM'}
					</div>
					<div>
						{day.pm ? (
							<span>{day.pm.activity || '—'}</span>
						) : (
							'—'
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default function GuidesPage({ isAdmin }) {
	const [search, setSearch] = useState('');
	const [guideList, setGuideList] = useState([]);
	const [loadingList, setLoadingList] = useState(false);
	const [activities, setActivities] = useState([]);

	const [selectedId, setSelectedId] = useState('');
	const [guide, setGuide] = useState(null);
	const [itinerary, setItinerary] = useState(null);
	const [loadingGuide, setLoadingGuide] = useState(false);

	const [dateFilter, setDateFilter] = useState('today');
	const [customDate, setCustomDate] = useState(todayYMD());
	const [inputDate, setInputDate] = useState(todayYMD());

	const [createOpen, setCreateOpen] = useState(false);
	const [newName, setNewName] = useState('');
	const [newActivityIds, setNewActivityIds] = useState([]);
	const [creating, setCreating] = useState(false);

	const [confirmDeleteId, setConfirmDeleteId] = useState(null);
	const [deleting, setDeleting] = useState(false);

	const [editingId, setEditingId] = useState(null);
	const [editName, setEditName] = useState('');
	const [editActivityIds, setEditActivityIds] = useState([]);
	const [saving, setSaving] = useState(false);

	const selectedRowRef = useRef(null);

	// Sync inputDate when customDate changes
	useEffect(() => {
		setInputDate(customDate);
	}, [customDate]);

	const sortedGuideList = useMemo(() => {
		return [...guideList].sort((a, b) => {
			const la = lastNameKey(a.name);
			const lb = lastNameKey(b.name);
			if (la !== lb) return la.localeCompare(lb);
			return (a.name || '').localeCompare(b.name || '');
		});
	}, [guideList]);

	const filteredGuideList = useMemo(() => {
		const q = search.toLowerCase().trim();
		return sortedGuideList.filter((g) => g.name.toLowerCase().includes(q));
	}, [sortedGuideList, search]);

	// Load guides and activities
	useEffect(() => {
		let cancelled = false;

		async function load() {
			setLoadingList(true);
			try {
				const [guidesRes, activitiesRes] = await Promise.all([
					fetch('/api/guides'),
					fetch('/api/activities'),
				]);
				const guidesJson = await guidesRes.json();
				const activitiesJson = await activitiesRes.json();
				if (!cancelled) {
					setGuideList(Array.isArray(guidesJson) ? guidesJson : []);
					setActivities(Array.isArray(activitiesJson) ? activitiesJson : []);
				}
			} catch {
				if (!cancelled) setGuideList([]);
			} finally {
				if (!cancelled) setLoadingList(false);
			}
		}

		load();
		return () => { cancelled = true; };
	}, []);

	// Scroll selected row into view
	useEffect(() => {
		if (!selectedId) return;
		if (loadingList) return;
		if (!selectedRowRef.current) return;
		const t = setTimeout(() => {
			selectedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}, 50);
		return () => clearTimeout(t);
	}, [selectedId, loadingList, guideList]);

	// Load selected guide and itinerary
	useEffect(() => {
		let cancelled = false;

		async function load() {
			if (!selectedId) {
				setGuide(null);
				setItinerary(null);
				return;
			}

			setLoadingGuide(true);
			try {
				const [guideRes, itinRes] = await Promise.all([
					fetch(`/api/guides/${selectedId}`),
					getItineraryUrl(),
				]);

				if (!guideRes.ok) {
					setGuide(null);
					setItinerary(null);
					setLoadingGuide(false);
					return;
				}

				const guideJson = await guideRes.json();
				const itinJson = itinRes ? await itinRes.json() : null;

				if (!cancelled) {
					setGuide(guideJson);
					setItinerary(itinJson);
				}
			} catch {
				if (!cancelled) {
					setGuide(null);
					setItinerary(null);
				}
			} finally {
				if (!cancelled) setLoadingGuide(false);
			}
		}

		async function getItineraryUrl() {
			const params = new URLSearchParams();
			if (dateFilter === 'date' && customDate) {
				params.set('date', customDate);
			} else if (dateFilter === 'today') {
				params.set('dateRange', 'today');
			} else if (dateFilter === 'week') {
				params.set('dateRange', 'week');
			}
			return fetch(`/api/guides/${selectedId}/itinerary?${params.toString()}`);
		}

		load();
		return () => { cancelled = true; };
	}, [selectedId, dateFilter, customDate]);

	async function handleCreate() {
		if (!newName.trim()) return;
		setCreating(true);
		try {
			const res = await fetch('/api/guides', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ name: newName.trim(), activityIds: newActivityIds }),
			});
			const json = await res.json();
			if (!res.ok) {
				alert(json?.error || 'Failed to add guide');
				return;
			}
			const refetch = await fetch('/api/guides');
			const fresh = await refetch.json();
			setGuideList(Array.isArray(fresh) ? fresh : []);
			setNewName('');
			setNewActivityIds([]);
			setCreateOpen(false);
		} catch {
			alert('Failed to add guide');
		} finally {
			setCreating(false);
		}
	}

	function startEdit(guide) {
		setEditingId(guide.id);
		setEditName(guide.name);
		setEditActivityIds(guide.activityIds || []);
	}

	function cancelEdit() {
		setEditingId(null);
		setEditName('');
		setEditActivityIds([]);
	}

	async function handleSave(guideId) {
		if (!editName.trim()) return;
		setSaving(true);
		try {
			const res = await fetch(`/api/guides/${guideId}`, {
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ name: editName.trim(), activityIds: editActivityIds }),
			});
			const json = await res.json();
			if (!res.ok) {
				alert(json?.error || 'Failed to save');
				return;
			}
			setGuideList((prev) =>
				prev.map((g) =>
					g.id === guideId
						? {
								...g,
								name: editName.trim(),
								activityIds: editActivityIds,
								activities: editActivityIds.map((id) => ({
									id,
									name: activities.find((a) => a.id === id)?.name || id,
								})),
						  }
						: g
				)
			);
			setEditingId(null);
		} catch {
			alert('Failed to save guide');
		} finally {
			setSaving(false);
		}
	}

	async function handleDelete(guideId) {
		setDeleting(true);
		try {
			const res = await fetch(`/api/guides/${guideId}`, { method: 'DELETE' });
			if (!res.ok) {
				const json = await res.json().catch(() => ({}));
				alert(json?.error || 'Failed to delete guide');
				return;
			}
			setGuideList((prev) => prev.filter((g) => g.id !== guideId));
			if (selectedId === guideId) setSelectedId('');
			setConfirmDeleteId(null);
		} catch {
			alert('Failed to delete guide');
		} finally {
			setDeleting(false);
		}
	}

	function toggleActivity(id) {
		setEditActivityIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	}

	function toggleActivityAdd(id) {
		setNewActivityIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	}

	function getScheduleUrl() {
		const params = new URLSearchParams();
		if (dateFilter === 'date' && customDate) {
			params.set('date', customDate);
		} else if (dateFilter === 'today') {
			params.set('dateRange', 'today');
		} else if (dateFilter === 'week') {
			params.set('dateRange', 'week');
		}
		const query = params.toString();
		const url = `${window.location.origin}/guide/${guide.id}${query ? `?${query}` : ''}`;
		return url;
	}

	async function handleShareSchedule() {
		try {
			await navigator.clipboard.writeText(getScheduleUrl());
			alert('Schedule link copied to clipboard!');
		} catch {
			alert('Failed to copy link');
		}
	}

	function handlePrintSchedule() {
		const url = getScheduleUrl();
		const printWindow = window.open(url, '_blank');
		if (printWindow) {
			printWindow.addEventListener('load', () => {
				setTimeout(() => {
					printWindow.print();
				}, 500);
			});
		}
	}

	return (
		<div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16, padding: 16, minHeight: '100vh' }}>
			{/* LEFT: guide list */}
			<div style={{ border: '1px solid #ddd', borderRadius: 12, padding: 12 }}>
				<div style={{ display: 'flex', gap: 8 }}>
					<input
						placeholder="Search guides…"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						style={{ flex: 1, padding: 10, borderRadius: 10, border: '1px solid #ccc' }}
					/>
					<button onClick={() => setCreateOpen(true)} disabled={!isAdmin}>
						+ Add
					</button>
				</div>

				<div style={{ marginTop: 12, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
					{loadingList && <div style={{ opacity: 0.7 }}>Loading…</div>}
					{!loadingList && filteredGuideList.length === 0 && (
						<div style={{ opacity: 0.6, fontSize: 13, marginTop: 10 }}>
							{guideList.length === 0 ? 'No guides yet.' : 'No matches.'}
						</div>
					)}
					{!loadingList &&
						filteredGuideList.map((g) => (
							<div
								key={g.id}
								ref={selectedId === g.id ? selectedRowRef : null}
								onClick={() => setSelectedId(g.id)}
								style={{
									padding: '10px 10px',
									borderRadius: 10,
									cursor: 'pointer',
									background: selectedId === g.id ? 'var(--selection-bg)' : 'transparent',
									fontWeight: selectedId === g.id ? 600 : 400,
								}}
							>
								{g.name}
							</div>
						))}
				</div>
			</div>

			{/* Create Guide Modal */}
			{createOpen && (
				<div
					role="dialog"
					aria-modal="true"
					style={{
						position: 'fixed',
						inset: 0,
						background: 'rgba(0, 0, 0, 0.35)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						zIndex: 1000,
						padding: 16,
					}}
					onClick={() => !creating && setCreateOpen(false)}
				>
					<div
						style={{
							background: 'var(--card)',
							borderRadius: 12,
							padding: 16,
							width: '100%',
							maxWidth: 360,
							boxShadow: '0 4px 16px rgba(32, 65, 81, 0.12)',
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<div style={{ fontWeight: 800, marginBottom: 16, fontSize: 16 }}>New Guide</div>

						<Field label="Name">
							<input
								type="text"
								placeholder="Guide name…"
								value={newName}
								onChange={(e) => setNewName(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') handleCreate();
								}}
							/>
						</Field>

						<div style={{ marginTop: 12 }}>
							<Field label="Available to Guide">
								<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
									{activities.map((a) => (
										<label
											key={a.id}
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: 5,
												padding: '4px 10px',
												borderRadius: 8,
												border: '1px solid var(--stroke)',
												background: newActivityIds.includes(a.id) ? 'var(--teal)' : 'var(--sand)',
												color: newActivityIds.includes(a.id) ? '#fff' : 'inherit',
												cursor: 'pointer',
												userSelect: 'none',
												fontSize: 13,
											}}
										>
											<input
												type="checkbox"
												checked={newActivityIds.includes(a.id)}
												onChange={() => toggleActivityAdd(a.id)}
												style={{ display: 'none' }}
											/>
											{a.name}
										</label>
									))}
									{activities.length === 0 && (
										<div style={{ opacity: 0.6 }}>No activities found</div>
									)}
								</div>
							</Field>
						</div>

						<div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
							<button onClick={handleCreate} disabled={creating || !newName.trim()}>
								{creating ? 'Adding…' : 'Add Guide'}
							</button>
							<button
								onClick={() => {
									setCreateOpen(false);
									setNewName('');
									setNewActivityIds([]);
								}}
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}

			{/* RIGHT: guide detail or itinerary */}
			<div style={{ border: '1px solid #ddd', borderRadius: 12, padding: 16, overflow: 'auto' }}>
				{!guide ? (
					<div style={{ opacity: 0.6, textAlign: 'center', paddingTop: 40 }}>
						{filteredGuideList.length === 0 ? (
							<div>No guides available.</div>
						) : loadingGuide ? (
							<div>Loading…</div>
						) : (
							<div>Select a guide to view schedule.</div>
						)}
					</div>
				) : isAdmin && editingId === guide.id ? (
					// Edit mode
					<div style={{ display: 'grid', gap: 14 }}>
						<div style={{ fontWeight: 800, fontSize: 16 }}>Edit Guide</div>

						<Field label="Name">
							<input
								type="text"
								value={editName || ''}
								onChange={(e) => setEditName(e.target.value)}
							/>
						</Field>

						<Field label="Available to Guide">
							<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
								{activities.map((a) => (
									<label
										key={a.id}
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 5,
											padding: '4px 10px',
											borderRadius: 8,
											border: '1px solid var(--stroke)',
											background: editActivityIds.includes(a.id) ? 'var(--teal)' : 'var(--sand)',
											color: editActivityIds.includes(a.id) ? '#fff' : 'inherit',
											cursor: 'pointer',
											userSelect: 'none',
											fontSize: 13,
										}}
									>
										<input
											type="checkbox"
											checked={editActivityIds.includes(a.id)}
											onChange={() => toggleActivity(a.id)}
											style={{ display: 'none' }}
										/>
										{a.name}
									</label>
								))}
							</div>
						</Field>

						<div style={{ display: 'flex', gap: 8 }}>
							<button onClick={() => handleSave(guide.id)} disabled={saving || !editName.trim()}>
								{saving ? 'Saving…' : 'Save'}
							</button>
							<button onClick={cancelEdit} disabled={saving}>
								Cancel
							</button>
						</div>
					</div>
				) : (
					// View mode
					<div style={{ display: 'grid', gap: 16 }}>
						<div style={{ border: '1px solid #ddd', borderRadius: 12, padding: 16 }}>
							<div style={{ fontSize: 20, fontWeight: 800 }}>{guide.name || 'Guide'}</div>
							{guide.activities?.length > 0 && (
								<div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
									{guide.activities.map((a) => (
										<span
											key={a.id}
											style={{
												fontSize: 12,
												padding: '2px 8px',
												borderRadius: 999,
												background: 'var(--teal)',
												color: '#fff',
												fontWeight: 600,
											}}
										>
											{a.name || a.id}
										</span>
									))}
								</div>
							)}
							<div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
								<button onClick={handleShareSchedule} style={{ fontSize: 12 }}>
									Share Schedule
								</button>
								<button onClick={handlePrintSchedule} style={{ fontSize: 12 }}>
									Print Schedule
								</button>
								{isAdmin && (
									<>
										{confirmDeleteId === guide.id ? (
											<>
												<span style={{ fontSize: 12, alignSelf: 'center', opacity: 0.8 }}>Delete?</span>
												<button
													onClick={() => handleDelete(guide.id)}
													disabled={deleting}
													style={{ fontSize: 12, background: 'crimson', color: '#fff', border: 'none' }}
												>
													{deleting ? '…' : 'Yes'}
												</button>
												<button
													onClick={() => setConfirmDeleteId(null)}
													disabled={deleting}
													style={{ fontSize: 12 }}
												>
													No
												</button>
											</>
										) : (
											<>
												<button onClick={() => startEdit(guide)} style={{ fontSize: 12 }}>
													Edit
												</button>
												<button onClick={() => setConfirmDeleteId(guide.id)} style={{ fontSize: 12 }}>
													Delete
												</button>
											</>
										)}
									</>
								)}
							</div>
						</div>

						{/* Date filter */}
						<div style={{ border: '1px solid #ddd', borderRadius: 12, padding: 12 }}>
							<div style={{ fontWeight: 700, marginBottom: 8 }}>View Schedule</div>
							<div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
								<select
									value={dateFilter}
									onChange={(e) => setDateFilter(e.target.value)}
									style={{ padding: 8, borderRadius: 8, border: '1px solid #ccc' }}
								>
									<option value="today">Today</option>
									<option value="week">This Week</option>
									<option value="date">Specific Date</option>
								</select>
								{dateFilter === 'date' && (
									<input
										type="date"
										value={inputDate}
										onChange={(e) => setInputDate(e.target.value)}
										onBlur={(e) => {
											if (e.target.value && e.target.value !== customDate) {
												setCustomDate(e.target.value);
											}
										}}
										style={{ padding: 8, borderRadius: 8, border: '1px solid #ccc' }}
									/>
								)}
							</div>
						</div>

						{/* Itinerary */}
						{loadingGuide ? (
							<div style={{ opacity: 0.7 }}>Loading schedule…</div>
						) : itinerary?.days?.length ? (
							<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
								{itinerary.days.map((day) => (
									<DayCard key={day.date} day={day} />
								))}
							</div>
						) : (
							<div style={{ opacity: 0.7, padding: 16, textAlign: 'center' }}>
								No scheduled activities for this period.
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
